import { authActions, authApiActions } from '@mkp/auth/actions';
import { objectKeys } from '@mkp/shared/util-format';
import { UserLoginMethodType } from '@mkp/tracking/models';
import { transientAppCacheActions } from '@mkp/transient-app-cache/actions';
import { loginActions } from '@mkp/user/feature-login/actions';
import { passwordlessLoginActions } from '@mkp/user/feature-passwordless-login/actions';
import { Action, ActionReducer, createFeature, createReducer, MetaReducer, on } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';

export enum TransientAppCacheItems {
  loginEmail = 'loginEmail',
  passwordlessLinkEmail = 'passwordlessLinkEmail',
  loginStarted = 'loginStarted',
  loginMethod = 'loginMethod',
  companyWasClaimed = 'companyWasClaimed',
}

// Items that should NOT be synced to localStorage (session-only)
const SESSION_ONLY_ITEMS: TransientAppCacheItems[] = [TransientAppCacheItems.loginEmail];

export interface TransientAppCacheState {
  items: {
    [TransientAppCacheItems.loginEmail]: string | undefined;
    [TransientAppCacheItems.passwordlessLinkEmail]: string | undefined;
    [TransientAppCacheItems.loginStarted]: Date | undefined;
    [TransientAppCacheItems.companyWasClaimed]: Date | undefined;
    [TransientAppCacheItems.loginMethod]: UserLoginMethodType | undefined;
  };
}

export const getInitialState = (): TransientAppCacheState => ({
  items: {
    [TransientAppCacheItems.loginEmail]: undefined,
    [TransientAppCacheItems.passwordlessLinkEmail]: undefined,
    [TransientAppCacheItems.loginStarted]: undefined,
    [TransientAppCacheItems.companyWasClaimed]: undefined,
    [TransientAppCacheItems.loginMethod]: undefined,
  },
});

const reducer = createReducer(
  getInitialState(),
  on(loginActions.usernamePasswordSubmitted, (state) => ({
    ...state,
    items: {
      ...state.items,
      [TransientAppCacheItems.loginStarted]: new Date(),
      [TransientAppCacheItems.loginMethod]: 'password' as UserLoginMethodType,
    },
  })),
  on(transientAppCacheActions.companyWasClaimed, (state) => ({
    ...state,
    items: { ...state.items, [TransientAppCacheItems.companyWasClaimed]: new Date() },
  })),
  on(transientAppCacheActions.loginStarted, (state, { email }) => ({
    ...state,
    items: {
      ...state.items,
      [TransientAppCacheItems.loginEmail]: email,
    },
  })),
  on(passwordlessLoginActions.linkRequested, (state, { email }) => ({
    ...state,
    items: {
      ...state.items,
      [TransientAppCacheItems.passwordlessLinkEmail]: email,
      [TransientAppCacheItems.loginStarted]: new Date(),
      [TransientAppCacheItems.loginMethod]: 'email' as UserLoginMethodType,
    },
  })),
  on(authApiActions.sendPasswordlessLinkSuccess, (state, { email }) => ({
    ...state,
    items: {
      ...state.items,
      [TransientAppCacheItems.passwordlessLinkEmail]: email,
    },
  })),
  on(transientAppCacheActions.companyWasClaimedUsed, (state) => ({
    ...state,
    items: { ...state.items, [TransientAppCacheItems.companyWasClaimed]: undefined },
  })),
  on(transientAppCacheActions.loginStartedUsed, (state) => ({
    ...state,
    items: { ...state.items, [TransientAppCacheItems.loginStarted]: undefined },
  })),
  on(authActions.verifiedUserAuthenticated, (state) => ({
    ...state,
    items: {
      ...state.items,
      [TransientAppCacheItems.loginEmail]: undefined,
      [TransientAppCacheItems.passwordlessLinkEmail]: undefined,
    },
  }))
);

export const transientAppCacheFeature = createFeature({
  name: 'transientAppCacheState',
  reducer,
});

const getKeysToSync = (): (keyof TransientAppCacheState['items'])[] => {
  const allItems = objectKeys(getInitialState().items);
  // Filter out session-only items from localStorage sync
  return allItems.filter((key) => !SESSION_ONLY_ITEMS.includes(key));
};

export const transientAppCacheReducer: MetaReducer = (
  reducer: ActionReducer<TransientAppCacheState, Action>
) =>
  localStorageSync({
    keys: [{ [transientAppCacheFeature.name]: [{ items: getKeysToSync() }] }],
    storageKeySerializer: (key) => `jobcloud.${key}`,
    rehydrate: true,
  })(reducer);
